import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, ShieldCheck, Clock, MapPin, KeyRound, LogOut } from 'lucide-react';
import { authApi } from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import useConfirm from '../useConfirm';
import { ConfirmDeleteDialog } from '../ConfirmDeleteDialog';

/**
 * SecurityCard — sits on the Settings → Profile tab above ChangePasswordCard.
 *
 * Shows:
 *   • Previous sign-in (timestamp + IP + user-agent) — useful for spotting
 *     unfamiliar logins.
 *   • Password last changed timestamp.
 *   • A "Sign out all other devices" button that hits
 *     /api/auth/sessions/revoke-others. The server bumps `token_generation`
 *     (which invalidates every other JWT issued for this user) and returns a
 *     fresh JWT for the current session — we swap it into `equipr_token` so
 *     the calling tab stays signed-in.
 */
const SecurityCard = () => {
  const { formatDateTime } = useSettings();
  const { confirm, dialogProps } = useConfirm();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(false);

  const load = async () => {
    try {
      const { data } = await authApi.getSecurity();
      setInfo(data);
    } catch {
      /* silent — card just stays empty */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRevoke = () => {
    confirm({
      title: 'Sign out all other devices?',
      description: "You'll stay signed in here. Every other browser and device will be signed out.",
      confirmLabel: 'Sign out others',
      testId: 'confirm-revoke-sessions',
      onConfirm: async () => {
        setRevoking(true);
        try {
          const { data } = await authApi.revokeOtherSessions();
          if (data?.token) localStorage.setItem('equipr_token', data.token);
          toast.success('All other sessions signed out.');
          await load();
        } catch (err) {
          const msg = err.response?.data?.detail;
          toast.error(typeof msg === 'string' ? msg : 'Could not revoke other sessions.');
          throw err;
        } finally {
          setRevoking(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <Card data-testid="security-card-loading">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="security-card">
      <CardHeader>
        <CardTitle className="uppercase tracking-wide flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" />
          Sign-in security
        </CardTitle>
        <CardDescription>
          Review recent activity and sign out other devices in one click.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <InfoTile
            icon={Clock}
            label="Previous sign-in"
            value={info?.previous_login_at ? formatDateTime(info.previous_login_at) : 'First sign-in'}
            sub={[
              info?.previous_login_ip && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {info.previous_login_ip}
                </span>
              ),
              info?.previous_login_user_agent && shortUserAgent(info.previous_login_user_agent),
            ].filter(Boolean)}
            testid="security-previous-login"
          />
          <InfoTile
            icon={KeyRound}
            label="Password last changed"
            value={info?.password_changed_at ? formatDateTime(info.password_changed_at) : 'Never'}
            testid="security-pwd-changed"
          />
        </div>

        <div className="pt-4 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Signed in on another device?</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              This will sign you out everywhere except this browser.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleRevoke}
            disabled={revoking}
            className="shrink-0"
            data-testid="revoke-sessions-btn"
          >
            {revoking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogOut className="w-4 h-4 mr-2" />}
            {revoking ? 'Signing out…' : 'Sign out all other devices'}
          </Button>
        </div>
      </CardContent>
      <ConfirmDeleteDialog {...dialogProps} />
    </Card>
  );
};

const InfoTile = ({ icon: Icon, label, value, sub = [], testid }) => (
  <div
    className="rounded-lg border border-border bg-card/40 px-4 py-3"
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
    <p className="text-sm font-medium">{value}</p>
    {sub.length > 0 && (
      <div className="text-xs text-muted-foreground mt-1 flex flex-col gap-0.5">
        {sub.map((s, i) => <div key={i}>{s}</div>)}
      </div>
    )}
  </div>
);

const shortUserAgent = (ua) => {
  if (!ua) return null;
  // Quick best-effort parser — readable summary, not a full UA library
  let browser = 'Unknown browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';

  let os = 'Unknown OS';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  return `${browser} · ${os}`;
};

export default SecurityCard;
